import { useState } from 'react'
import { useAuditLogs } from '@/hooks/useAudit'
import { DataTable } from '@/components/common/DataTable'
import { SearchInput } from '@/components/common/SearchInput'
import { FilterDropdown } from '@/components/common/FilterDropdown'
import { StatusBadge } from '@/components/common/StatusBadge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { AuditLog, AuditFilter, AUDIT_ACTIONS, AUDIT_TARGETS } from '@/types'
import { formatDateTime } from '@/lib/utils'
import { Eye } from 'lucide-react'

const actionOptions = [
  { value: 'all', label: 'Todas' },
  ...AUDIT_ACTIONS.map((a) => ({ value: a, label: a })),
]

const targetOptions = [
  { value: 'all', label: 'Todos' },
  ...AUDIT_TARGETS.map((t) => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) })),
]

function JsonDiff({ label, data }: { label: string; data: Record<string, unknown> }) {
  if (!data || !Object.keys(data).length) {
    return (
      <div>
        <p className="mb-1 text-sm font-medium">{label}</p>
        <p className="text-xs italic text-muted-foreground">Sin datos</p>
      </div>
    )
  }

  return (
    <div>
      <p className="mb-1 text-sm font-medium">{label}</p>
      <pre className="max-h-48 overflow-auto rounded-md bg-muted p-3 font-mono text-xs">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  )
}

export const AuditLogPage = () => {
  const [filters, setFilters] = useState<AuditFilter>({ page: 1, limit: 20 })
  const [actorSearch, setActorSearch] = useState('')
  const [actionFilter, setActionFilter] = useState('all')
  const [targetFilter, setTargetFilter] = useState('all')
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null)

  const currentFilters: AuditFilter = {
    ...filters,
    actor: actorSearch || undefined,
    action: actionFilter !== 'all' ? actionFilter : undefined,
    target_type: targetFilter !== 'all' ? targetFilter : undefined,
  }

  const { data, isLoading } = useAuditLogs(currentFilters)

  const columns = [
    {
      key: 'actor',
      header: 'Actor',
      cell: (log: AuditLog) => (
        <div>
          <p className="text-sm font-medium">{log.actor}</p>
          <p className="text-xs capitalize text-muted-foreground">{log.actor_role}</p>
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Accion',
      cell: (log: AuditLog) => (
        <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{log.action}</span>
      ),
    },
    {
      key: 'target',
      header: 'Objetivo',
      cell: (log: AuditLog) => (
        <div>
          <StatusBadge status={log.target_type} />
          <p className="mt-0.5 max-w-[120px] truncate font-mono text-xs text-muted-foreground">
            {log.target_id}
          </p>
        </div>
      ),
    },
    {
      key: 'timestamp',
      header: 'Fecha',
      cell: (log: AuditLog) => (
        <span className="text-sm text-muted-foreground">{formatDateTime(log.timestamp)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      cell: (log: AuditLog) => (
        <Button variant="ghost" size="sm" onClick={() => setSelectedLog(log)}>
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Audit Log</h1>

      <div className="flex flex-wrap items-center gap-4">
        <SearchInput
          placeholder="Buscar por actor..."
          value={actorSearch}
          onChange={(v) => {
            setActorSearch(v)
            setFilters((f) => ({ ...f, page: 1 }))
          }}
          className="w-64"
        />
        <FilterDropdown
          label="Accion"
          options={actionOptions}
          value={actionFilter}
          onChange={(v) => {
            setActionFilter(v)
            setFilters((f) => ({ ...f, page: 1 }))
          }}
        />
        <FilterDropdown
          label="Tipo"
          options={targetOptions}
          value={targetFilter}
          onChange={(v) => {
            setTargetFilter(v)
            setFilters((f) => ({ ...f, page: 1 }))
          }}
        />
        {data && (
          <span className="ml-auto text-sm text-muted-foreground">
            {data.total} registro{data.total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <DataTable
        columns={columns}
        data={data?.logs ?? []}
        isLoading={isLoading}
        keyExtractor={(log) => log.id}
        pagination={
          data
            ? {
                page: data.page,
                pages: data.pages,
                onPageChange: (page) => setFilters((f) => ({ ...f, page })),
              }
            : undefined
        }
        emptyMessage="No hay registros de auditoria"
      />

      {/* Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalle de auditoria</DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-muted-foreground">Actor</dt>
                  <dd className="font-medium">{selectedLog.actor}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Rol</dt>
                  <dd className="capitalize">{selectedLog.actor_role}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Accion</dt>
                  <dd className="font-mono">{selectedLog.action}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Objetivo</dt>
                  <dd>
                    {selectedLog.target_type}: {selectedLog.target_id}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Fecha</dt>
                  <dd>{formatDateTime(selectedLog.timestamp)}</dd>
                </div>
                {selectedLog.ip_address && (
                  <div>
                    <dt className="text-muted-foreground">IP</dt>
                    <dd className="font-mono text-xs">{selectedLog.ip_address}</dd>
                  </div>
                )}
              </dl>

              <div className="grid gap-4 md:grid-cols-2">
                <JsonDiff label="Antes" data={selectedLog.changes?.before ?? {}} />
                <JsonDiff label="Despues" data={selectedLog.changes?.after ?? {}} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
